import { useState } from "react";
import { Loader2, Plus, X, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

export function DevTestPhotos({ projectId, test, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const photos = test.photos || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande (máximo 5 MB).");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data: up } = await api.post("/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.post(`/dev-projects/${projectId}/tests/${test.id}/photos`, {
        photo_path: up.path,
      });
      onSaved(data);
      toast.success("Foto anexada ao teste.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (path) => {
    try {
      const { data } = await api.delete(
        `/dev-projects/${projectId}/tests/${test.id}/photos?photo_path=${encodeURIComponent(path)}`
      );
      onSaved(data);
      toast.success("Foto removida. O teste continua salvo.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="mt-3 border-t border-white/10 pt-3" data-testid={`test-photos-${test.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Fotos do teste {photos.length > 0 && `(${photos.length})`}
        </p>
        <label
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] text-muted-foreground transition-colors hover:border-brand/40 hover:text-foreground"
          data-testid={`add-photo-label-${test.id}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Adicionar foto
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={upload}
            disabled={busy}
            data-testid={`add-photo-input-${test.id}`}
          />
        </label>
      </div>

      {photos.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Nenhuma foto nesta tentativa ainda.
        </p>
      ) : (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {photos.map((p) => (
            <div key={p} className="group relative shrink-0" data-testid={`test-photo-${test.id}`}>
              <button
                type="button"
                onClick={() => setZoom(p)}
                className="block h-20 w-20 overflow-hidden rounded-lg border border-white/10"
              >
                <img src={fileUrl(p)} alt="Foto do teste" className="h-full w-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center bg-background/60 opacity-0 transition-opacity group-hover:opacity-100">
                  <ZoomIn className="h-4 w-4" />
                </span>
              </button>
              <button
                type="button"
                onClick={() => remove(p)}
                data-testid={`remove-photo-btn-${test.id}`}
                className="absolute -right-1.5 -top-1.5 rounded-full border border-white/15 bg-background p-1 text-muted-foreground transition-colors hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {zoom && (
        <ResponsiveModal
          open
          onOpenChange={(o) => !o && setZoom(null)}
          title={`Teste #${test.attempt}`}
          description="Foto registrada nesta tentativa."
          testid="test-photo-lightbox"
        >
          <img
            src={fileUrl(zoom)}
            alt="Foto do teste"
            className="mt-2 max-h-[70vh] w-full rounded-xl object-contain"
          />
          <Button
            variant="outline"
            onClick={() => setZoom(null)}
            className="mt-4 h-11 w-full rounded-full"
            data-testid="close-lightbox-btn"
          >
            Fechar
          </Button>
        </ResponsiveModal>
      )}
    </div>
  );
}
